import { useState } from 'react';
import type { Ref } from 'react';
import { useImperativeHandle } from 'react';

import { ampli } from '~entities/ampli';

import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@platform-ui/dataTable';

import type { ColumnDef, SortDirection } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type {
  ColumnActionOptions,
  RowActionOptions,
  ColumnSizingOptions,
} from './lib/table-options';
import type { DashboardTableState } from './lib/table-state';
import { HeaderCell } from './ui/header-cell/header-cell';

export type DashboardTableRef = {
  setPage: (page: number) => void;
};

const sortDirection = {
  asc: 'desc',
  desc: 'asc',
};

type Props<T> = {
  screen: string;
  columns: ColumnDef<T, any>[];
  isPaginationEnabled: boolean;
  data?: T[];
  totalAmount?: number;
  defaultState?: DashboardTableState;
  onStateChange?: (newState: DashboardTableState) => void;
  // react-table sizing doesn't work with ui-kit components =>
  // this prop is used to set columns width instead
  colSizingOptions?: Record<string, ColumnSizingOptions>;
  rowActionOptions?: RowActionOptions<T>;
  colActionOptions?: Record<string, ColumnActionOptions>;
  rowsPerPageOptions?: [number, number, number];
  innerRef?: Ref<DashboardTableRef>;
};

const defaultPagination = {
  page: 0,
  rowsPerPage: 10,
};

export function DashboardTable<TData>({
  screen,
  columns,
  data = [],
  totalAmount = data.length,
  isPaginationEnabled,
  defaultState = {},
  onStateChange,
  colSizingOptions = {},
  rowActionOptions = {},
  colActionOptions = {},
  rowsPerPageOptions = [10, 20, 30],
  innerRef,
}: Props<TData>) {
  const [{ sort, pagination = defaultPagination }, setState] =
    useState<DashboardTableState>(defaultState);

  const table = useReactTable<TData>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  useImperativeHandle(
    innerRef,
    () => ({
      setPage: (newPage) =>
        setState((oldState) => ({
          ...oldState,
          pagination: {
            ...(oldState.pagination || defaultPagination),
            page: newPage,
          },
        })),
    }),
    []
  );

  const handleStateChange = (newState: DashboardTableState) => {
    onStateChange?.(newState);
    setState(newState);
  };

  const handleSortingChange = (columnId: string) => {
    const direction =
      sort?.orderBy === columnId
        ? (sortDirection[sort.direction] as SortDirection)
        : 'desc';
    const newSort = { orderBy: columnId, direction };

    if (colActionOptions[columnId]?.sendAnalyticsEvent) {
      ampli.track({ event_type: `${screen}_${columnId}_sort_tap` });
    }
    handleStateChange({
      pagination: { ...pagination, page: 0 },
      sort: newSort,
    });
  };

  return (
    <>
      <Table borders="all-rounded" tableLayout="fixed">
        <colgroup>
          {table.getAllColumns().map((column) => (
            <col key={column.id} style={colSizingOptions[column.id] ?? {}} />
          ))}
        </colgroup>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <HeaderCell
                    key={header.id}
                    header={header}
                    isSortable={
                      !!colActionOptions[header.column.id]?.isSortable
                    }
                    sort={sort}
                    onSortChange={handleSortingChange}
                  />
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              hoverable={rowActionOptions.isHoverable}
              clickable={!!rowActionOptions.onClick}
              onClick={() => rowActionOptions.onClick?.(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} size="large">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isPaginationEnabled && (
        <Pagination
          page={pagination.page}
          itemsTotal={totalAmount}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          renderItemsCount={(current, total) => (
            <span>
              Items on&nbsp;the&nbsp;page{current}&nbsp;from&nbsp;{total}
            </span>
          )}
          renderPagesTotal={(pagesTotal) => (
            <span>Total pages:&nbsp;{pagesTotal}</span>
          )}
          onChangePage={(_event, { page }) =>
            handleStateChange({
              pagination: { ...pagination, page },
              sort,
            })
          }
          onChangeRowsPerPage={(_event, { rowsPerPage }) =>
            handleStateChange({
              pagination: { page: 0, rowsPerPage },
              sort,
            })
          }
        />
      )}
    </>
  );
}
